import { Component, computed, inject, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { ActivationStart, Router } from '@angular/router';
import { filter, finalize, tap } from 'rxjs';
import { BaseComponent } from '@/app/components/common/baseComponent/base.component';
import { HelpUseCase } from '@/app/_core/application/help/help.use-cases';
import { HelpService } from '@/app/components/common/help/help.service';
import { ScreenService } from '@/services/screen/screen.service';
import { RoutingPaths } from '@/app/common/routing';
import { GeneralHelpModeConst } from '@/app/components/common/help/general-help/config/general-help.const';
import { GeneralHelpConst, LayoutConfig, PanelName } from './layout.const';
import { Layout } from './layout.model';
import { CONFIG_JSON_KEYS } from '@/app/_core/domain/const/config-json-keys.const';
import { AppConfiguration } from '@/app/conf/app.configuration';
import { parseSplitterSize } from './layout.utils';
import { WaitLoadingService } from '@/app/components/common/waitLoading/wait-loading.service';
import { HelpType } from '@/app/components/common/help/_config/help-type.const';

@Component({ template: '' })
export abstract class LayoutAbstractComponent extends BaseComponent {
  private readonly _deviceDetectorService = inject(ScreenService);
  private readonly _helpService = inject(HelpService);

  public readonly waitService = inject(WaitLoadingService);
  public readonly appConfig = inject(AppConfiguration);
  public readonly helpUseCases = inject(HelpUseCase);
  public readonly router = inject(Router);

  public readonly isUserAdmin: Signal<boolean> = computed(() => this.helpUseCases.selectHelpSig()?.isUserAdmin);
  public readonly isGeneralHelp: Signal<boolean> = computed(() => !this.helpUseCases.selectHelpSig()?.isGeneralHelp);
  public readonly currentTagSig: Signal<string> = computed(() => this.helpUseCases.selectHelpSig()?.currentTag ?? '');
  public readonly pageTagsSig: Signal<string[]> = computed(() => (this.helpUseCases.selectHelpSig()?.tagByActualView ?? '').split(',').filter(Boolean));
  public readonly canGoBackSig: Signal<boolean> = computed(() => (this.helpUseCases.selectHelpSig()?.history?.length ?? 0) > 1);
  public readonly canGoForwardSig: Signal<boolean> = computed(() => (this.helpUseCases.selectHelpSig()?.future?.length ?? 0) > 0);
  public readonly groupedHelpSig: Signal<any[]> = computed(() => this._helpService.groupHelp(this.helpUseCases.selectHelpSig()?.listData));
  public readonly isDesktop: Signal<boolean> = computed(() => this._deviceDetectorService.isDesktopSig());

  public readonly sidePanelWidthSig: Signal<string> = computed(() => {
    const splitterSizeArray = this.panelSizeSig();
    const sideSize = splitterSizeArray[1] || 30;
    return `${sideSize}%`;
  });

  public readonly sideSubPanelWidthSig: Signal<string> = computed(() => {
    const splitterSizeArray = this.panelSizeSig();
    const sideSize = Math.max(0, (splitterSizeArray[1] || 30) - 2);
    return `${sideSize}%`;
  });

  public readonly isHelpOpen: Signal<boolean> = computed(() => {
    const helpSig = this.helpUseCases.selectHelpSig();
    if (helpSig?.isOpenHelp === false) return false;
    return !!helpSig?.isOpenHelp;
  });

  public readonly searchTagsSig: Signal<string[]> = computed(() => {
    const helpSig = this.helpUseCases.selectHelpSig();
    return helpSig?.context?.searchTags ?? (helpSig?.tagByActualView ?? '').split(',').filter(Boolean);
  });

  public readonly historyItemsSig: Signal<any[]> = computed(() => {
    const history = this.helpUseCases.selectHelpSig()?.history || [];
    return history
      .slice(0, -1)
      .map((item, index) => ({
        label: this.getHistoryLabel(item),
        value: { index, item }
      }))
      .reverse();
  });

  public readonly panelSizeSig: Signal<number[]> = computed(
    () => {
      const rawSplitterSize = this.appConfig.getValue(CONFIG_JSON_KEYS.splitterLayoutSize);
      const defaultConfigSize = parseSplitterSize(rawSplitterSize);

      const savedSize = this.helpUseCases.selectHelpSig()?.panelSize;
      if (savedSize && Array.isArray(savedSize) && savedSize.length === 2) {
        const minMainSize = defaultConfigSize[0];
        if (savedSize[0] < minMainSize) {
          return [minMainSize, 100 - minMainSize];
        }
        return savedSize;
      }
      return defaultConfigSize;
    },
    { equal: (a, b) => a[0] === b[0] && a[1] === b[1] }
  );

  protected _lastPanelSizes: number[] = [99.999, 0.001];

  public layoutConfig: Signal<Layout> = computed(
    () => {
      const isDesktop = this.isDesktop();
      const isHelpOpen = this.isHelpOpen();
      const splitterSizeArray = this.panelSizeSig();
      const isDragging = this.openedThisDrag();

      if (isDesktop) {
        const rawSplitterSize = this.appConfig.getValue(CONFIG_JSON_KEYS.splitterLayoutSize);
        const defaultConfigSize = parseSplitterSize(rawSplitterSize);
        const minMainSize = defaultConfigSize[0];

        let currentSizes;
        if (isDragging) {
          currentSizes = this._lastPanelSizes;
        } else {
          currentSizes = isHelpOpen ? splitterSizeArray : [99.999, 0.001];
          this._lastPanelSizes = currentSizes;
        }

        return { panel: LayoutConfig.panel, panelSizes: currentSizes, minSizes: [minMainSize, 0.001] };
      } else {
        return { panel: [{ panel: PanelName.MAIN_PANEL }], panelSizes: [99.999, 0.001], minSizes: [99.999, 0.001] };
      }
    },
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
  );

  public readonly currentPanelSizeSig: Signal<number> = computed(() => this.panelSizeSig()[1] || 30);
  public readonly isOpenHelp: Signal<boolean> = computed(() => this.helpUseCases.selectHelpSig()?.isOpenHelp);
  public isGeneralRoute: WritableSignal<boolean> = signal(this.router.url.includes(RoutingPaths.LoggedInGeneralHelp));

  public readonly refreshCounter: WritableSignal<number> = signal(0);
  public readonly openedThisDrag: WritableSignal<boolean> = signal(false);
  public readonly searchingSig: WritableSignal<boolean> = signal(false);
  public readonly hasSubheaderSig: WritableSignal<boolean> = signal(false);

  public generalHelpConst = GeneralHelpConst;
  public selectHelpMode = GeneralHelpModeConst;

  constructor() {
    super();
    this.controlRoute();
  }

  protected controlRoute(): void {
    this.subscribe(this.router.events.pipe(filter((event) => event instanceof ActivationStart)), (event: ActivationStart) => {
      this.isGeneralRoute.set(event.snapshot.routeConfig?.path.includes(RoutingPaths.LoggedInGeneralHelp));
    });
  }

  protected controlGetHelp(): void {
    const helpSig = this.helpUseCases.selectHelpSig();
    const tags = untracked(() => this.searchTagsSig());
    const tagsStr = tags.toString();

    if (!tagsStr || this.searchingSig()) {
      return;
    }

    const isRefreshAction = this.refreshCounter() > 0 || helpSig?.extraData?.isRefreshCounter;

    if (!isRefreshAction) {
      this.searchingSig.set(false);
      return;
    }

    this.searchingSig.set(true);
    if (!this.isGeneralRoute()) {
      this._getHelp(tags);
    }
  }

  protected _getHelp(tags: string[]): void {
    this.subscribe(
      this._helpService.searchContextHelpTags(tags).pipe(
        tap(() => this.searchingSig.set(true)),
        finalize(() => this.searchingSig.set(false))
      ),
      (list) => {
        const tagToSet = tags.toString();
        const currentConfig = this.helpUseCases.selectHelpSig();
        const history = [...(currentConfig.history || [])];

        if (list.length > 0) {
          if (history.length === 0) {
            history.push({ tags: tags, data: list });
          } else {
            const lastIndex = history.length - 1;
            if (history[lastIndex].tags.toString() === tagToSet) {
              history[lastIndex].data = list;
            } else {
              history.push({ tags: tags, data: list });
            }
          }
        }

        this.helpUseCases.setHelp({
          ...currentConfig,
          listData: list,
          currentTag: tagToSet,
          isGeneralHelp: tagToSet === HelpType.GENERAL_HELP ? currentConfig.isGeneralHelp : false,
          isCloseHelp: false,
          history: history,
          future: currentConfig.future || [],
          extraData: { ...currentConfig.extraData, isRefreshCounter: false }
        });
        this.refreshCounter.set(0);
      },
      () => {
        this.searchingSig.set(false);
      }
    );
  }

  private getHistoryLabel(item: any): string {
    if (item.selectedNodeId) return item.selectedNodeId;
    if (item.data && item.data.length > 0) {
      return item.data[0].shortDesc || item.tags.toString();
    }
    return item.tags.toString();
  }
}
